<template>
  <div class="container">
    <BLoading :active.sync="isLoading" />
    <div v-if="!isLoading && enableLTNewFlow">
      <LawnTreatmentView
        :property-id="property_id"
        :pricing="selectedProperty.priced.services[str_id].options[0]"
        :service-promo-id="servicePromoId"
      />
    </div>
    <div v-if="!isLoading && !enableLTNewFlow" class="columns">
      <div class="column is-6">
        <OldLawnTreatmentView
          v-if="str_id === 'fertilization'"
          :fertilization-count="fertilizationCount"
        />
        <AerationView v-if="str_id === 'aeration'" />
      </div>
      <div class="column is-5 is-offset-1">
        <form novalidate @submit.prevent="addService">
          <h2 v-if="str_id === 'fertilization'" class="order-priced-services-form--h2">
            Add a Lawn Treatment Package
          </h2>

          <LsAddressCard
            :property="selectedProperty"
            class="order-priced-services-form--address-card"
          />
          <div class="order-priced-services-form--cost">
            <h3 class="order-priced-services-form--h3">Cost</h3>
            <p>
              {{ price }} <span>per service</span>
              <span
                v-if="str_id === 'fertilization'"
                class="order-priced-services-form--fertilization-count"
                >({{ fertilizationCount }} service package)</span
              >
            </p>
          </div>
          <LsRadios
            v-model="starts_at"
            label="When would you like to start?"
            track-id="order_priced_services_form-starts_at"
            name="starts_at"
            :options="startDateOptions"
            class="order-priced-services-form--starts-at"
          />
          <div class="order-priced-services-form--charge-info">
            <p>
              Your {{ serviceName }} will occur within a three week window starting on the Monday
              you select. We’re unable to offer call-aheads or advance notice of your
              {{ serviceName }} service.
              <span v-if="str_id === 'fertilization'">
                Each service will be scheduled about 4-6 weeks apart and you can check your app for
                the next service window.
              </span>
            </p>
            <p>As always, your card is never charged until after the service is completed.</p>
          </div>
          <div class="columns">
            <div class="column"></div>
            <div class="column is-narrow">
              <LsButton
                :text="`ADD ${serviceName}`"
                :loading="orderServices.loadingStatus.isUpdating"
                type="submit"
                track-id="order_priced_services_form-add_service"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { lsFilters, lsRouterHelpers } from 'ls-vue-common';
import {
  PROPERTIES__SELECTED_PROPERTY,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  ORDER_SERVICES__SUBMIT_PRICED_LTRS_SERVICE,
} from '@/store/types';
import _ from 'lodash';
import { mapGetters, mapActions, mapState } from 'vuex';
import OldLawnTreatmentView from './LawnTreatmentView/LawnTreatmentView.old.vue';
import LawnTreatmentView from './LawnTreatmentView/LawnTreatmentView.vue';
import AerationView from './AerationView/AerationView.vue';
import { LsRadios, LsAddressCard, LsButton } from '@/components/common';
import { lsOrderServicesHelpers } from '@/helpers';

export default {
  name: 'OrderPricedServicesFormView',
  components: {
    AerationView,
    LawnTreatmentView,
    LsAddressCard,
    LsButton,
    LsRadios,
    OldLawnTreatmentView,
  },

  data() {
    return {
      property_id: lsRouterHelpers.getParam(this.$route, 'property_id'),
      str_id: lsRouterHelpers.getParam(this.$route, 'str_id').toLowerCase(),
      price_id: '',
      price: '',
      starts_at: '',
      service_id: '',
      serviceName: '',
      startDateOptions: [],
      startDates: [],
    };
  },
  computed: {
    ...mapState(['orderServices', 'properties', 'featureFlags']),
    ...mapGetters([PROPERTIES__SELECTED_PROPERTY]),
    isLoading() {
      return (
        this.properties.loadingStatus.isLoading ||
        this.orderServices.loadingStatus.isLoading ||
        _.isNil(this.selectedProperty)
      );
    },
    enableLTNewFlow() {
      return this.featureFlags.flags['customer.lt_new_flow'] && this.str_id === 'fertilization';
    },
    servicePromoId() {
      const promoId = lsRouterHelpers.getParam(this.$route, 'service_promo_id');
      return promoId ? _.toNumber(promoId) : null;
    },
    selectedProperty() {
      return this[PROPERTIES__SELECTED_PROPERTY](this.property_id);
    },
    fertilizationCount() {
      const count = this.selectedProperty && this.selectedProperty.ltrs_fertilization_count;
      return count || 8;
    },
  },
  async created() {
    await this[PROPERTIES__GET_PROPERTIES_AND_SCHEDULES]();

    if (
      _.isNil(this.selectedProperty) ||
      (this.str_id !== 'fertilization' && this.str_id !== 'aeration')
    ) {
      return this.$router.push({
        name: 'services',
      });
    }

    this.generateServiceProperties();
    this.generateStartDateOptions();
  },
  methods: {
    ...mapActions([
      PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
      ORDER_SERVICES__SUBMIT_PRICED_LTRS_SERVICE,
    ]),
    addService() {
      const service_promo_id = lsRouterHelpers.getParam(this.$route, 'service_promo_id');

      this[ORDER_SERVICES__SUBMIT_PRICED_LTRS_SERVICE]({
        property_id: this.property_id,
        service_id: this.service_id,
        price_id: this.price_id,
        starts_at: this.starts_at,
        service_promo_id: service_promo_id ? _.toNumber(service_promo_id) : null,
        onSuccess: () => {
          this.$router.push({ name: 'services' });
        },
      });
    },
    generateServiceProperties() {
      const pricing = this.selectedProperty.priced.services[this.str_id].options[0];
      this.price = lsFilters.currency({ amount: pricing.price });
      this.price_id = pricing.price_id;
      if (this.str_id === 'aeration') {
        this.serviceName = 'aeration';
        this.service_id = 5;
      } else if (this.str_id === 'fertilization') {
        this.serviceName = 'lawn treatment';
        this.service_id = 2;
      }
    },

    generateStartDateOptions() {
      this.startDateOptions = lsOrderServicesHelpers.generateLtrsStartDateOptions();
      this.starts_at = this.startDateOptions[0].value;
    },
  },
};
</script>

<style lang="scss" scoped>
.order-priced-services-form--add-service {
  justify-content: flex-end;
}
.order-priced-services-form--cost {
  border-bottom: 1px solid $primary-light;
  border-top: 1px solid $primary-light;
  padding: 13px 0px;
  margin: 13px 0px;

  span {
    font-weight: normal;
  }
  p {
    font-size: $size-4;
    font-weight: bold;
    color: $primary;
  }
}
.order-priced-services-form--charge-info {
  background-color: $primary-light;
  margin-bottom: 30px;
  padding: 23px;
  p {
    font-style: italic;
  }
  p:last-child {
    margin-top: 17px;
    font-weight: bold;
  }
}
.order-priced-services-form--starts-at {
  padding: 8px 0px;
}
.order-priced-services-form--h2 {
  font-weight: bold;
  margin-bottom: 30px;
  font-size: $size-4;
  margin-top: 4px;
}
.order-priced-services-form--h3 {
  font-size: $size-5;
  font-weight: bold;
  padding-bottom: 6px;
}

.order-priced-services-form--fertilization-count {
  margin-left: 5px;
}

.themed-app {
  .order-priced-services-form--cost {
    border-bottom-color: $theme-color-primary-light;
    border-top-color: $theme-color-primary-light;

    p {
      font-size: $theme-txt-body-lg;
      color: $theme-color-primary;
    }
  }

  .order-priced-services-form--charge-info {
    background-color: $theme-color-primary-light;
  }

  .order-priced-services-form--h2 {
    font-size: $theme-txt-body-lg;
  }

  .order-priced-services-form--h3 {
    font-size: $theme-txt-body;
  }
}
</style>
